import { Component, inject } from '@angular/core';
import { MethodBase } from '../base/method-base';
import { AuthMethodViewModel, FidoAuthMethodViewModel } from '../../../../services/identity';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { FidoHelpersService } from '../../../enrollment/fido-helpers.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UsernamelessModalComponent } from './usernameless-modal/usernameless-modal.component';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-method-fido',
    templateUrl: './fido.component.html',
    styleUrl: './fido.component.scss',
    imports: [ReactiveFormsModule, DatePipe, TranslatePipe]
})
export class FidoComponent extends MethodBase {
  private fb = inject(FormBuilder);
  private fidoHelpers = inject(FidoHelpersService);
  private translateService = inject(TranslateService);
  private toastrService = inject(ToastrService);
  private ngbModal = inject(NgbModal);


  private fidoMethod: FidoAuthMethodViewModel;

  smsValidators!: ValidatorFn | null;
  emailValidators!: ValidatorFn | null;

  isRegistering!: boolean;

  hasCredentials: boolean = false;

  isSmsEnable: boolean = true;
  isEmailEnable: boolean = true;

  removedTokens: string[] = [];

  get credentials(): FormArray {
    return this.form.controls['credentials'] as FormArray;
  }

  get backupMethod(): FormControl {
    return this.form.controls['backupMethod'] as FormControl;
  }

  get sms(): FormControl {
    return this.form.controls['sms'] as FormControl;
  }
  get email(): FormControl {
    return this.form.controls['email'] as FormControl;
  }

  override ngOnInit(): void {
    super.ngOnInit();
      this.setup();
      this.monitorFormStatus();
    if(!this.hasCredentials && !this.isSelf) {
      this.formStatusChanged.emit({ sequence: this.method.sequence, index: 0, status: 'VALID', touched: this.isEditing });
    }
  }
  setup(): void {
    this.fidoMethod = this.method as FidoAuthMethodViewModel;

    if (this.method.sequence != 1) {
      this.options.state.fido.allowResidentKey = false;
    }

    this.smsValidators = Validators.compose([Validators.required, Validators.pattern(this.options.validators.regPhoneNumber)]);
    this.emailValidators = Validators.compose([Validators.required, Validators.pattern(this.options.validators.regEmail)]);

    this.form = this.fb.group({
      credentials: !this.fidoMethod.credentials ? this.fb.array([]) : this.fb.array(this.fidoMethod.credentials.map(x => this.fb.group({
        id: [x.id],
        name: [x.name, Validators.required],
        regDate: [x.regDate],
        allowUsernameless: [x.isUsernameless],
        optionsId: [null],
        attestation: [null]
      }))),
      backupMethod: [null, Validators.required],
      sms: ['', this.smsValidators],
      email: ['', this.emailValidators]
    });

    if (!this.isSelf) {
      for (let cred of this.credentials.controls as any) {
        cred.controls['name'].disable()
      }  
    }

    this.subscriptions.add(this.backupMethod.valueChanges.subscribe((method) => {
      this.updateBackupMethod(method);
    }));
    
    this.setValue(this.fidoMethod, false);
    this.updateBackupMethod(this.backupMethod.value);
  }
  cancel(): void {

  }

  updateBackupMethod(method: string) {
    if (method === 'SMS') {
      this.email.disable({ emitEvent: false });
      this.sms.enable({ emitEvent: false });
    }
    else if (method === 'Email') {
      this.sms.disable({ emitEvent: false });
      this.email.enable({ emitEvent: false });
    }
    this.email.updateValueAndValidity({ emitEvent: false });
    this.sms.updateValueAndValidity({ emitEvent: false });
  }

  getMethod(): AuthMethodViewModel {
    this.fidoMethod.credentials = this.credentials.getRawValue().filter(x => !x.attestation).map(x => ({
      id: x.id,
      name: x.name,
      regDate: x.regDate,
      isUsernameless: x.allowUsernameless
    }));
    this.fidoMethod.attestations = this.credentials.getRawValue().filter(x => x.attestation);
    this.fidoMethod.attestations.forEach(x => x.attestation.type = 'PublicKey'); //Fix for .Net model binding
    this.fidoMethod.backup = {
      method: this.form?.value?.backupMethod,
      recipient: this.form?.value?.backupMethod == 'SMS' ? this.form?.value?.sms : this.form?.value?.email
    }
    return this.fidoMethod;
  }

  setValue(val: any, emitEvent: boolean = true): void {
    this.backupMethod.setValue(val.backup?.method, { emitEvent: emitEvent });
    if (val.backup?.method == 'SMS') {
      this.sms.setValue(val.backup?.recipient, { emitEvent: emitEvent });
    } else {
      this.email.setValue(val.backup?.recipient, { emitEvent: emitEvent });
    }


    if (!this.sms.value || !this.email.value) {
      this.getUserDetails(emitEvent);
    }
  }

  getUserDetails(emitEvent: boolean = true): void {
    if (!this.sms.value) {
      this.sms.setValue(this.options.user.phone, { emitEvent: emitEvent });
    }
    if (!this.email.value) {
      this.email.setValue(this.options.user.email, { emitEvent: emitEvent });
    }
  }

  promptUsernameless(): void {
    if (this.options.state.fido.allowResidentKey) {
      const modalRef = this.ngbModal.open(UsernamelessModalComponent, {
        size: 'md',
        windowClass: 'fadeInUp animated huge',
        backdrop: 'static',
        keyboard: false,
      });

      modalRef.result.then((userResponse: boolean) => {
        this.registerFidoToken(userResponse);
      })
    } else {
      this.registerFidoToken(false);
    }

  }

  registerFidoToken(isUsernameless: boolean): void {
    this.isRegistering = true;

    if (!this.sms.value || !this.email.value) {
      this.getUserDetails();
    }
    this.subscriptions.add(
      this.options.functions.fido.getCredentialOptions(this.options.org.mnemonic, this.options.user.username, isUsernameless).subscribe(async model => {
        await this.handleCredentialOptions(model, isUsernameless)
      })
    )
  }

  async handleCredentialOptions(model: any, isUsernameless: boolean): Promise<void> {
    let data = model.options;
    if (data.status !== 'ok') {
      console.log('error', data.errorMessage);
      return;
    }
    data.excludeCredentials = data.excludeCredentials.filter((x: any) => this.removedTokens.indexOf(x.id) == -1);
    for (let cred of this.credentials.value) {
      const id = cred.id;
      if (data.excludeCredentials.indexOf(id) == -1) {
        data.excludeCredentials.push({ type: 'public-key', id: id });
      }
    }
    let options = this.fidoHelpers.formatCredentialOptions(data);
    let newCredential;
    try {
      newCredential = await navigator.credentials.create({ publicKey: options });
    }
    catch (e: any) {
      if (e.toString().indexOf('contains one of the credentials already registered') > 0) {
        this.toastrService.error(this.translateService.instant('MyProfile.FidoAlreadyInUseMessage'), this.translateService.instant('MyProfile.FidoAlreadyInUse'), { timeOut: 8000 });
      } else {
        this.toastrService.error(e, this.translateService.instant('Action.Error'), { timeOut: 8000 });
      }
      console.log('Error creating credentials in browser', e);
    }
    this.isRegistering = false;

    const formattedCredential = this.fidoHelpers.formatCredential(newCredential);
    if (!this.options.state.fido.allowMultipleTokens) {
      this.credentials.clear();
    }
    const fg = this.fb.group({
      id: [model.id],
      optionsId: [model.id],
      name: [this.translateService.instant('MyProfile.DefaultTokenName', { index: this.credentials.length + 1 }), Validators.required],
      allowUsernameless: [isUsernameless],
      attestation: [formattedCredential, Validators.required]
    })
    this.credentials.push(fg);
  }

  removeToken(index: number): void {
    this.options.modals.fido.removeToken().result.then((userResponse: boolean) => {
      if (userResponse) {
        const cred = this.credentials.at(index).value;
        this.removedTokens.push(cred.id);
        this.credentials.removeAt(index)
      }
    })
  }

  identifyToken(index: number): void {
    this.options.modals.fido.identifyToken().result.then((userResponse: boolean) => {
      if (userResponse) {
        const cred = this.credentials.at(index).value;
        this.subscriptions.add(
          this.options.functions.fido.identifyToken(this.options.org.mnemonic, this.options.user.username, `"${cred.id}"`).subscribe(async data => {
            const options = this.fidoHelpers.formatAssertionOptions(data);
            try {
              await navigator.credentials.get({ publicKey: options })
            }
            catch (err) {
              this.toastrService.error(this.translateService.instant('Action.Error'), this.translateService.instant('Action.Error'), { timeOut: 8000 });
              console.log(err);
            }
          })
        )
      }
    })
  }
}
